import React from "react"
import { BookOpen } from "lucide-react"
import TopicPageLayout from "../topic-page-layout"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "../../../../ui/accordion"
import { Card, CardContent } from "../../../../ui/card"

const GettingStarted = () => {
  const faqItems = [
    {
      question: "How do I create my account?",
      answer:
        "To create your account, click the 'Sign Up' button in the top right corner of the homepage. You'll need to provide your email address, create a password, and complete a short verification process."
    },
    {
      question: "What information do I need to complete my profile?",
      answer:
        "To complete your profile, you'll need to provide your full name, a profile photo, your location, and a short bio. You may also need to provide additional verification documents depending on your creator tier."
    },
    {
      question: "How long does account verification take?",
      answer:
        "Standard account verification typically takes 1-2 business days. For higher tier accounts that require additional verification, the process may take 3-5 business days."
    },
    {
      question: "Can I use the platform while my account is being verified?",
      answer:
        "Yes, you can browse the platform and set up your profile while your account is being verified, but you won't be able to publish content or receive payments until verification is complete."
    }
  ]

  const quickTips = [
    "Complete your profile 100% to increase visibility",
    "Enable two-factor authentication for extra security",
    "Connect your social media accounts to grow your audience",
    "Upload a high-quality profile picture and cover image"
  ]

  return (
    <TopicPageLayout
      title="Getting Started"
      description="Everything you need to hit the ground running with our platform. Learn how to set up your account, complete verification, and start creating."
      icon={<BookOpen className="h-8 w-8 text-[#3D2A60]" />}
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            First Steps
          </h2>
          <p className="text-gray-600 mb-6">
            Welcome to our platform! We're excited to have you join our
            community of creators. This guide will walk you through the
            essential steps to get started and make the most of your
            experience.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-medium mb-3 text-[#3D2A60]">
                  1. Create Your Account
                </h3>
                <p className="text-gray-600">
                  Sign up using your email address or through your Google or
                  Facebook account. Complete the verification process to
                  unlock all features.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-medium mb-3 text-[#3D2A60]">
                  2. Set Up Your Profile
                </h3>
                <p className="text-gray-600">
                  Add a profile photo, cover image, bio, and customize your
                  page to reflect your brand and personal style.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-medium mb-3 text-[#3D2A60]">
                  3. Configure Payment Details
                </h3>
                <p className="text-gray-600">
                  Connect your bank account or payment provider to ensure you
                  can receive payments from your supporters.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-medium mb-3 text-[#3D2A60]">
                  4. Create Your First Content
                </h3>
                <p className="text-gray-600">
                  Upload your first piece of content and set your visibility
                  and pricing options to start engaging with your audience.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Quick Tips
          </h2>
          <ul className="list-disc pl-5 space-y-2 text-gray-600">
            {quickTips.map((tip, index) => (
              <li key={index}>{tip}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-[#3D2A60]">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-gray-600">{item.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </div>
    </TopicPageLayout>
  )
}

export default GettingStarted
